import React, { useState, ChangeEvent, useCallback, useMemo } from 'react';
import { UsfType } from 'spw-redux/modules/calculators/types';
import { SelectInputProps } from '@mui/material/Select/SelectInput';
import {
  DialogHeading,
  LargeTextField,
  SummaryCalculationsDialogActions,
  TotalAvailableUSFDialog,
  TotalAvailableUSFDialogContent,
  TotalAvailableUsfParagraph,
  CustomSelect,
} from 'spw-components/dialogs/total-program/styles';
import { SPWSaveButton } from 'spw-shared/globalComponents/buttons';
import { SPWCancelButton } from 'spw-shared/globalComponents';
import { Box, MenuItem } from '@mui/material';
import { globalInputLabelProps } from 'spw-shared/globalComponents/forms';
import { asUsf, handleOnKeyPress } from 'spw-shared/utils';
import { useDispatch } from 'react-redux';
import {
  setCurrentScenario,
  setTotalProgramAvailableUsf,
} from 'spw-redux/modules/calculators/actions';
import { cond, isNaN } from 'lodash';
import { always, equals, T } from 'ramda';
import { generateLinkedScenariosIdsMap } from 'spw-components/programming-scenarios/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { MAX_TOTAL_USF, TOTAL_PROGRAM_USF_TYPES } from 'spw-constants';
import { PlainProgramScenario } from '../../../models/Scenario';
import TotalProgramScenario from '../../../models/TotalProgramScenario';

interface TotalAvailableUsfModalProps {
  totalProgram: PlainProgramScenario;
  scenarios: PlainProgramScenario[];
  showDialog: boolean;
  setShowDialog?: (showDialog: boolean) => void;
  setEditFactor: (editFactor: string) => void;
}

const TOTAL_USF_VALUES = {
  SUM_ALL_LINKED_DEPARTMENTS: TOTAL_PROGRAM_USF_TYPES.sumLinkedScenarios,
  TOTAL_PROGRAM_ONLY: TOTAL_PROGRAM_USF_TYPES.totalProgramOnly,
};

const SELECT_OPTIONS: { value: string; text: string }[] = [
  {
    value: TOTAL_USF_VALUES.SUM_ALL_LINKED_DEPARTMENTS,
    text: 'Sum of all linked departments',
  },
  { value: TOTAL_USF_VALUES.TOTAL_PROGRAM_ONLY, text: 'Total Program only' },
];

const getModalText = (option: string, areaUnit: string) => {
  return cond([
    [
      equals(TOTAL_USF_VALUES.SUM_ALL_LINKED_DEPARTMENTS),
      () => (
        <div>
          This value is the sum of all Total Available {areaUnit} subtotals from
          the linked department scenarios.
        </div>
      ),
    ],
    [
      equals(TOTAL_USF_VALUES.TOTAL_PROGRAM_ONLY),
      () => (
        <div>
          This is not a global change and will not update the Total Available{' '}
          {areaUnit} in the linked department scenarios.
        </div>
      ),
    ],
    [T, always(<></>)],
  ])(option);
};

const TotalAvailableUsfModal: React.FC<TotalAvailableUsfModalProps> = ({
  totalProgram,
  scenarios,
  showDialog,
  setShowDialog,
  setEditFactor,
}) => {
  const dispatch = useDispatch();
  const { usfType } = totalProgram;

  const { getUnit, convertToOriginalUnit, convertToUnit } = useUnitConversion();
  const [totalUsfOption, setTotalUsfOption] = useState(
    usfType || TOTAL_USF_VALUES.SUM_ALL_LINKED_DEPARTMENTS
  );

  const linkedScenariosIdsMap = generateLinkedScenariosIdsMap(totalProgram);

  const scenariosUsf = useMemo(() => {
    return (
      scenarios?.reduce((usfSum: number, scenario: PlainProgramScenario) => {
        if (linkedScenariosIdsMap[scenario.id]) {
          return usfSum + scenario.totalAvailableUSF;
        }
        return usfSum;
      }, 0) || 0
    );
  }, [scenarios, linkedScenariosIdsMap]);

  const initialUSF =
    usfType === TOTAL_USF_VALUES.SUM_ALL_LINKED_DEPARTMENTS
      ? scenariosUsf
      : totalProgram?.totalAvailableUSF || 0;

  const [totalUsfAvailable, setTotalUsfAvailable] = useState(
    convertToUnit(initialUSF, { unitType: 'area', formatter: (v) => asUsf(v) })
  );

  const onClose = () => {
    setEditFactor('');
    setShowDialog(false);
  };

  const onSave = () => {
    let usf = parseFloat(totalUsfAvailable.toString().replace(/,/g, ''));
    usf = convertToOriginalUnit(usf, { unitType: 'area' });

    if (isNaN(usf)) {
      usf = 0;
    }
    const associatedScenarios = scenarios.filter(
      (s) => linkedScenariosIdsMap[s.id]
    );
    dispatch(
      setCurrentScenario(
        {
          ...TotalProgramScenario.toAPITotalProgram(totalProgram),
          usfType: totalUsfOption as UsfType,
        },
        associatedScenarios
      )
    );

    dispatch(setTotalProgramAvailableUsf(usf));

    onClose();
  };

  const handleUsfInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const usf = parseFloat(inputValue.replace(/,/g, ''));

    if (usf > MAX_TOTAL_USF) {
      setTotalUsfAvailable(asUsf(MAX_TOTAL_USF));
    } else if (usf > 0) {
      setTotalUsfAvailable(asUsf(usf));
    } else {
      setTotalUsfAvailable('');
    }
  };

  const handleSelectChange = useCallback<
    SelectInputProps<{ name: string; value: string }>['onChange']
  >(
    (event) => {
      const newUsfType = event.target.value;
      setTotalUsfOption(newUsfType as UsfType);
      let sourceUSF: number;
      if (event.target.value !== TOTAL_USF_VALUES.SUM_ALL_LINKED_DEPARTMENTS) {
        sourceUSF = totalProgram?.totalAvailableUSF || 0;
      } else {
        sourceUSF = scenariosUsf;
      }
      setTotalUsfAvailable(
        convertToUnit(sourceUSF, {
          unitType: 'area',
          formatter: (v) => asUsf(v),
        }) as string
      );
    },
    [totalProgram, scenariosUsf, convertToUnit]
  );

  return (
    <TotalAvailableUSFDialog
      open={showDialog}
      onClose={onClose}
      disableScrollLock
    >
      <TotalAvailableUSFDialogContent>
        <DialogHeading style={{ marginTop: 0, marginBottom: 30 }}>
          Total Available {getUnit('area')}
        </DialogHeading>

        <Box>
          <CustomSelect
            style={{ width: '100%', marginBottom: 25 }}
            value={totalUsfOption}
            onChange={handleSelectChange}
            data-testid="tps_usf_mode_input"
          >
            {SELECT_OPTIONS.map((option) => (
              <MenuItem key={`item-${option.value}`} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </CustomSelect>
        </Box>

        <Box>
          <LargeTextField
            disabled={
              totalUsfOption === TOTAL_USF_VALUES.SUM_ALL_LINKED_DEPARTMENTS
            }
            InputLabelProps={globalInputLabelProps}
            variant="outlined"
            value={totalUsfAvailable}
            onKeyPress={handleOnKeyPress}
            onChange={handleUsfInputChange}
            style={{ marginBottom: 16 }}
            inputProps={{
              name: 'tps_usf_input',
            }}
          />
        </Box>

        <TotalAvailableUsfParagraph>
          {getModalText(totalUsfOption, getUnit('area'))}
        </TotalAvailableUsfParagraph>

        <SummaryCalculationsDialogActions>
          <SPWSaveButton onClick={onSave} />
          <SPWCancelButton onClick={onClose} />
        </SummaryCalculationsDialogActions>
      </TotalAvailableUSFDialogContent>
    </TotalAvailableUSFDialog>
  );
};

export default TotalAvailableUsfModal;
